import type { ErrorInfo, ReactNode } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import type { TFunction } from 'react-i18next';
import * as Sentry from '@sentry/react';

import ErrorPage from './ErrorPage';

type Props = {
  children: ReactNode;
  rerouteTo: string;
  t: TFunction;
};

const ReactErrorBoundary = ({ children, rerouteTo, t }: Props) => {
  const defaults = {
    title: 'Unexpected error',
    message: 'Please refresh (f5) your browser to resolve the issue',
    button: 'Return home',
  };

  const Fallback = (
    <ErrorPage
      title={t('errors:boundary.title') ?? defaults.title}
      message={t('errors:boundary.message') ?? defaults.message}
      buttonLabel={t('errors:boundary.button') ?? defaults.button}
      buttonDestination={rerouteTo}
      withButton
    />
  );

  const onError = (error: Error, errorInfo: ErrorInfo) => {
    Sentry.withScope((scope) => {
      scope.setExtra('info', errorInfo);
      Sentry.captureException(error);
    });
  };

  return (
    <ErrorBoundary fallback={Fallback} onError={onError}>
      {children}
    </ErrorBoundary>
  );
};

export default ReactErrorBoundary;
